import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Phone, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import ModelsSection from '../components/sections/ModelsSection';
import ADUPackagesSection from '../components/sections/ADUPackagesSection';
import HowItWorksSection from '../components/sections/HowItWorksSection';
import TurnkeyServicesSection from '../components/sections/TurnkeyServicesSection';
import PortfolioSection from '../components/sections/PortfolioSection';
import ComparisonSection from '../components/sections/ComparisonSection';
import FAQSection from '../components/sections/FAQSection';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import ScrollingIcons from '../components/sections/ScrollingIcons';
import HeroLeadForm from '../components/HeroLeadForm';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getWebSiteSchema, getFAQPageSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_IMAGES = [
  'https://static.wixstatic.com/media/2de339_6c14e4a4ed3447ebac68c93238edfb7c~mv2.jpg',
  'https://static.wixstatic.com/media/2de339_f3ce4aabaed84a7ca8a8d31de05e715b~mv2.jpg',
  'https://static.wixstatic.com/media/2de339_6325f42a5d7247e39937c50314579fb3~mv2.jpg',
  'https://static.wixstatic.com/media/2de339_b9193103f6d84a08895c9a7e2616e90a~mv2.jpg',
];

const Home = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideImages, setSlideImages] = useState(DEFAULT_IMAGES);

  // Load slides from API
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/slides`);
        if (r.data.slides?.length > 0) {
          setSlideImages(r.data.slides.map(s => s.image));
        }
      } catch {}
    };
    load();
  }, []);

  // Build slides from dynamic images
  const heroSlides = slideImages.map(image => ({
    image,
    title: t('hero.title'), subtitle: t('hero.subtitle'),
    link: '/estimate', btnText: t('hero.freeEstimate'), showBrowse: true
  }));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentSlide = heroSlides[currentImageIndex];

  // SEO content based on language
  const seoTitle = isZh
    ? '加州 ADU 后院屋专家 | 30+ 年经验 1500+ 项目 | ALIGCUS 阿里建筑'
    : 'Custom ADU Builder in Los Angeles & SoCal | ALIGCUS Construction (1500+ ADUs Built)';
  const seoDesc = isZh
    ? 'ALIGCUS 阿里建筑 — 加州持照 ADU 后院屋专家。30+ 年经验、1500+ 项目落地。设计·审批·建造·交付一站式服务，覆盖南加 / 北加州。免费咨询 (626) 803-8000。'
    : "California's premier ADU builder. 30+ years experience, 1500+ ADUs delivered. Design, permit, build & deliver — all-in-one. Free SoCal & NorCal estimates: (626) 803-8000 / (408) 900-6688.";
  const seoCanonical = isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/';
  const orgSchema = getOrganizationSchema(isZh ? 'zh' : 'en');
  const websiteSchema = getWebSiteSchema(isZh ? 'zh' : 'en');
  const faqSchema = getFAQPageSchema(t);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={seoCanonical}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/' },
          { lang: 'en-US', url: 'https://www.aligcus.com/' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/' },
          { lang: 'zh-Hans', url: 'https://www.aligcus.com/zh/' },
        ]}
        schemas={[orgSchema, websiteSchema, faqSchema]}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen md:h-screen overflow-y-auto md:overflow-hidden">
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => {
            // SEO-friendly alt text — describes ADU + city for Google Image search
            const altTextEn = [
              'Modern detached ADU exterior in Los Angeles, CA — ALIGCUS Construction',
              'Custom 2-bedroom ADU floor plan with open kitchen — ALIGCUS California',
              'Newly built backyard ADU in San Gabriel Valley — ALIGCUS',
              'Luxury ADU interior living room in Pasadena, CA — ALIGCUS',
              '500 sqft 1-bedroom ADU exterior in Bay Area — ALIGCUS Construction',
            ];
            const altTextZh = [
              '洛杉矶现代独立 ADU 后院屋外观 — ALIGCUS 阿里建筑',
              '两房 ADU 户型图开放式厨房 — 加州 ALIGCUS 建造',
              '圣盖博谷新建后院屋 ADU — ALIGCUS 阿里建筑',
              '帕萨迪纳豪华 ADU 室内客厅 — ALIGCUS 阿里建筑',
              '湾区 500 平尺一房 ADU 外观 — ALIGCUS 阿里建筑',
            ];
            const altList = isZh ? altTextZh : altTextEn;
            const alt = altList[index % altList.length];
            return (
              <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}>
                <img
                  src={slide.image}
                  alt={alt}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index === 0 ? 'high' : 'low'}
                />
                <div className="absolute inset-0 bg-black/40" />
              </div>
            );
          })}
        </div>

        <div className="relative z-10 h-full flex items-center justify-center cursor-pointer block" onClick={() => navigate(currentSlide.link)}>
          <div className="text-center px-6 max-w-5xl" onClick={(e) => e.stopPropagation()}>
            {currentSlide.badge && (
              <div className="inline-block px-4 py-2 bg-red-600 rounded-full text-sm font-semibold text-white mb-6">{currentSlide.badge}</div>
            )}
            <h1 className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-testid="hero-title">
              {currentSlide.title}
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-white mb-6 sm:mb-8 font-light">{currentSlide.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
              <Button size="lg" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-red-600 hover:border-red-600 font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-lg transition-all duration-300" onClick={() => navigate('/design')}>
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {isZh ? '设计房屋' : 'Design MyHome'}
              </Button>
              <Button size="lg" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-red-600 hover:border-red-600 font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-lg transition-all duration-300" data-testid="hero-estimate-btn" onClick={() => navigate(currentSlide.link)}>
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {currentSlide.btnText}
              </Button>
              {currentSlide.showBrowse && (
                <Button size="lg" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-red-600 hover:border-red-600 font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-lg transition-all duration-300" onClick={() => document.getElementById('models')?.scrollIntoView({ behavior: 'smooth' })}>
                  {t('hero.browseModels')}
                </Button>
              )}
            </div>
            {/* Mobile Lead Form - below buttons */}
            <div className="flex justify-center mt-6 md:hidden">
              <HeroLeadForm />
            </div>
          </div>
        </div>

        {/* PC Lead Form - right side */}
        <div className="absolute top-1/2 right-4 sm:right-6 -translate-y-1/2 z-20 hidden md:block">
          <HeroLeadForm />
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => setCurrentImageIndex(i)} className={`h-2 rounded-full transition-all duration-300 ${i === currentImageIndex ? 'w-8 bg-red-600' : 'w-2 bg-white/60 hover:bg-white'}`} />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 text-center">
            <div className="p-6 sm:p-10 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="mb-3"><span className="text-4xl sm:text-6xl font-black text-red-600">30</span><span className="text-2xl sm:text-3xl font-bold text-red-600 align-top">+</span></div>
              <p className="text-gray-500 italic text-base sm:text-lg" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Years Builder Heritage</p>
            </div>
            <div className="p-6 sm:p-10 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="mb-3"><span className="text-4xl sm:text-6xl font-black text-red-600">1500</span><span className="text-2xl sm:text-3xl font-bold text-red-600 align-top">+</span></div>
              <p className="text-gray-500 italic text-base sm:text-lg" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>ADUs Delivered</p>
            </div>
            <div className="p-6 sm:p-10 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="mb-3"><span className="text-3xl sm:text-5xl font-black text-red-600">Multiple</span></div>
              <p className="text-gray-500 italic text-base sm:text-lg" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Model Homes Open</p>
            </div>
          </div>
        </div>
      </section>

      <ModelsSection />
      <ADUPackagesSection />
      <HowItWorksSection />
      <PortfolioSection />
      <ComparisonSection />
      <FAQSection />
      <TestimonialsSection />
      <ScrollingIcons />
      <Footer />
    </div>
  );
};

export default Home;
